// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';
import { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router-dom';
import Text from 'antd/lib/typography/Text';
import { Row, Col } from 'antd/lib/grid';
import Button from 'antd/lib/button';
import notification from 'antd/lib/notification';
import { MoreOutlined } from '@ant-design/icons';
import Dropdown from 'antd/lib/dropdown';
import Progress from 'antd/lib/progress';
import moment from 'moment';
import { getCore } from 'cvat-core-wrapper';

import ActionsMenuContainer from 'containers/actions-menu/actions-menu';
import CVATTooltip from 'components/common/cvat-tooltip';
import Preview from 'components/common/preview';
import { ActiveInference, PluginComponent } from 'reducers';
import AutomaticAnnotationProgress from './automatic-annotation-progress';

const core = getCore();

type TranscriptionQueueState = 'Queued' | 'Started' | 'Finished' | 'Failed';

interface TranscriptionStatusPayload {
    state: TranscriptionQueueState;
    message?: string;
    progress?: number;
}

export interface TaskItemProps {
    taskInstance: any;
    deleted: boolean;
    hidden: boolean;
    activeInference: ActiveInference | null;
    taskNamePlugins: PluginComponent[];
    cancelAutoAnnotation(): void;
}

interface TaskItemState {
    transcriptionStatus: TranscriptionStatusPayload | null;
    retryingTranscription: boolean;
}

class TaskItemComponent extends React.PureComponent<TaskItemProps & RouteComponentProps, TaskItemState> {
    private transcriptionPollTimeout: number | null;

    private isUnmounted: boolean;

    public constructor(props: TaskItemProps & RouteComponentProps) {
        super(props);
        this.transcriptionPollTimeout = null;
        this.isUnmounted = false;
        this.state = {
            transcriptionStatus: null,
            retryingTranscription: false,
        };
    }

    public componentDidMount(): void {
        this.updateTranscriptionStatus().catch(() => {
            // noop
        });
    }

    public componentDidUpdate(prevProps: TaskItemProps): void {
        const { taskInstance } = this.props;
        if (prevProps.taskInstance.id !== taskInstance.id) {
            this.clearTranscriptionPolling();
            this.setState({
                transcriptionStatus: null,
                retryingTranscription: false,
            });
            this.updateTranscriptionStatus().catch(() => {
                // noop
            });
        }
    }

    public componentWillUnmount(): void {
        this.isUnmounted = true;
        this.clearTranscriptionPolling();
    }

    private clearTranscriptionPolling(): void {
        if (this.transcriptionPollTimeout !== null) {
            window.clearTimeout(this.transcriptionPollTimeout);
            this.transcriptionPollTimeout = null;
        }
    }

    private scheduleTranscriptionPolling(): void {
        this.clearTranscriptionPolling();
        this.transcriptionPollTimeout = window.setTimeout(() => {
            this.updateTranscriptionStatus().catch(() => {
                // noop
            });
        }, 5000);
    }

    private normalizeTranscriptionStatus(status: any): TranscriptionStatusPayload {
        const state = ['Queued', 'Started', 'Finished', 'Failed'].includes(status?.state) ?
            status.state as TranscriptionQueueState :
            'Started';
        const message = typeof status?.message === 'string' ? status.message : '';
        const progress = typeof status?.progress === 'number' ? status.progress : 0;
        return { state, message, progress };
    }

    private async updateTranscriptionStatus(): Promise<void> {
        const { taskInstance } = this.props;
        try {
            const status = this.normalizeTranscriptionStatus(await core.tasks.transcriptionStatus(taskInstance.id));
            if (this.isUnmounted) {
                return;
            }

            this.setState({
                transcriptionStatus: status,
            });

            if (status.state === 'Queued' || status.state === 'Started') {
                this.scheduleTranscriptionPolling();
            } else {
                this.clearTranscriptionPolling();
            }
        } catch (error) {
            if (this.isUnmounted) {
                return;
            }

            this.setState({
                transcriptionStatus: {
                    state: 'Started',
                    message: 'Unable to fetch transcription status. Retrying...',
                    progress: 0,
                },
            });
            this.scheduleTranscriptionPolling();
        }
    }

    private getRetryDisabledReason(): string {
        const { transcriptionStatus, retryingTranscription } = this.state;

        if (retryingTranscription) {
            return 'Transcription retry is being queued.';
        }

        if (!transcriptionStatus) {
            return 'Transcription status is being checked.';
        }

        if (transcriptionStatus.state === 'Queued') {
            return 'Transcription is already queued.';
        }

        if (transcriptionStatus.state === 'Started') {
            return 'Transcription is already in progress.';
        }

        if (transcriptionStatus.state === 'Finished') {
            return 'Retry is available only after transcription fails.';
        }

        return '';
    }

    private async retryTranscription(): Promise<void> {
        const { taskInstance } = this.props;
        const { retryingTranscription, transcriptionStatus } = this.state;

        if (retryingTranscription || transcriptionStatus?.state !== 'Failed') {
            return;
        }

        this.setState({
            retryingTranscription: true,
        });

        try {
            const status = this.normalizeTranscriptionStatus(await core.tasks.retryTranscription(taskInstance.id));
            if (this.isUnmounted) {
                return;
            }

            this.setState({
                transcriptionStatus: status,
            });

            if (status.state === 'Queued' || status.state === 'Started') {
                this.scheduleTranscriptionPolling();
            } else {
                this.clearTranscriptionPolling();
            }

            notification.success({
                message: `Task "${taskInstance.name}" transcription retried`,
                description: status.message || 'Transcription retry has been queued.',
            });
        } catch (error) {
            if (this.isUnmounted) {
                return;
            }

            notification.error({
                message: `Could not retry transcription for "${taskInstance.name}"`,
                description: error instanceof Error ? error.message : String(error),
            });
        } finally {
            if (!this.isUnmounted) {
                this.setState({
                    retryingTranscription: false,
                });
            }
        }
    }

    private renderPreview(): JSX.Element {
        const { taskInstance } = this.props;
        return (
            <Col span={4}>
                <Preview
                    task={taskInstance}
                    loadingClassName='cvat-task-item-loading-preview'
                    emptyPreviewClassName='cvat-task-item-empty-preview'
                    previewWrapperClassName='cvat-task-item-preview-wrapper'
                    previewClassName='cvat-task-item-preview'
                />
            </Col>
        );
    }

    private renderDescription(): JSX.Element {
        // Task info
        const { taskInstance, taskNamePlugins } = this.props;
        const { id } = taskInstance;
        const owner = taskInstance.owner ? taskInstance.owner.username : null;
        const updated = moment(taskInstance.updatedDate).fromNow();
        const created = moment(taskInstance.createdDate).format('MMMM Do YYYY');

        // Get and truncate a task name
        const name = `${taskInstance.name.substring(0, 70)}${taskInstance.name.length > 70 ? '...' : ''}`;

        return (
            <Col span={10} className='cvat-task-item-description'>
                <Text strong type='secondary' className='cvat-item-task-id'>{`#${id}: `}</Text>
                <Text strong className='cvat-item-task-name'>
                    {name}
                </Text>
                { taskNamePlugins.map(({ component: Component }, index) => <Component key={index} />) }
                <br />
                {owner && (
                    <>
                        <Text type='secondary'>{`Created ${owner ? `by ${owner}` : ''} on ${created}`}</Text>
                        <br />
                    </>
                )}
                <Text type='secondary'>{`Last updated ${updated}`}</Text>
            </Col>
        );
    }

    private renderProgress(): JSX.Element {
        const { taskInstance, activeInference, cancelAutoAnnotation } = this.props;
        // Count number of jobs and performed jobs
        const numOfJobs = taskInstance.progress.totalJobs;
        const numOfCompleted = taskInstance.progress.completedJobs;

        // Progress appearance depends on number of jobs
        let progressColor = null;
        let progressText = null;
        if (numOfCompleted && numOfCompleted === numOfJobs) {
            progressColor = 'cvat-task-completed-progress';
            progressText = (
                <Text strong className={progressColor}>
                    Completed
                </Text>
            );
        } else if (numOfCompleted) {
            progressColor = 'cvat-task-progress-progress';
            progressText = (
                <Text strong className={progressColor}>
                    In Progress
                </Text>
            );
        } else {
            progressColor = 'cvat-task-pending-progress';
            progressText = (
                <Text strong className={progressColor}>
                    Pending
                </Text>
            );
        }

        const jobsProgress = numOfCompleted / numOfJobs;
        return (
            <Col span={6}>
                <Row justify='space-between' align='top'>
                    <Col>
                        <svg height='8' width='8' className={progressColor}>
                            <circle cx='4' cy='4' r='4' strokeWidth='0' />
                        </svg>
                        {progressText}
                    </Col>
                    <Col>
                        <Text type='secondary'>{`${numOfCompleted} of ${numOfJobs} jobs`}</Text>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <Progress
                            className={`${progressColor} cvat-task-progress`}
                            percent={jobsProgress * 100}
                            strokeColor='#1890FF'
                            showInfo={false}
                            strokeWidth={5}
                            size='small'
                        />
                    </Col>
                </Row>
                <AutomaticAnnotationProgress
                    activeInference={activeInference}
                    cancelAutoAnnotation={cancelAutoAnnotation}
                />
                {this.renderTranscriptionProgress()}
            </Col>
        );
    }

    private renderTranscriptionProgress(): JSX.Element | null {
        const { transcriptionStatus } = this.state;

        if (!transcriptionStatus) {
            return (
                <Row className='cvat-task-transcription-status'>
                    <Col span={24}>
                        <Text type='secondary'>Transcription: checking status...</Text>
                    </Col>
                </Row>
            );
        }

        const statusColor = {
            Queued: 'cvat-task-transcription-queued',
            Started: 'cvat-task-transcription-started',
            Finished: 'cvat-task-transcription-finished',
            Failed: 'cvat-task-transcription-failed',
        }[transcriptionStatus.state];
        const statusLabel = {
            Queued: 'Queued',
            Started: 'In progress',
            Finished: 'Completed',
            Failed: 'Failed',
        }[transcriptionStatus.state];
        const statusMessage = transcriptionStatus.message ?
            `${transcriptionStatus.message.substring(0, 60)}${transcriptionStatus.message.length > 60 ? '...' : ''}` :
            '';
        const progressPercent = (() => {
            if (transcriptionStatus.state === 'Finished' || transcriptionStatus.state === 'Failed') {
                return 100;
            }

            if (transcriptionStatus.state === 'Started') {
                const reportedProgress = Number.isFinite(transcriptionStatus.progress) ?
                    Math.round(transcriptionStatus.progress as number) :
                    0;
                return Math.max(reportedProgress, 5);
            }

            return 0;
        })();
        const progressStatus = {
            Queued: 'active',
            Started: 'active',
            Finished: 'success',
            Failed: 'exception',
        }[transcriptionStatus.state] as 'active' | 'success' | 'exception';

        return (
            <div className='cvat-task-transcription-status'>
                <Row justify='space-between' align='top'>
                    <Col>
                        <Text strong className={statusColor}>
                            {`Transcription: ${statusLabel}`}
                        </Text>
                    </Col>
                    <Col>
                        <Text type='secondary' className='cvat-task-transcription-message'>
                            {statusMessage}
                        </Text>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <Progress
                            className={`cvat-task-transcription-progress ${statusColor}`}
                            percent={progressPercent}
                            status={progressStatus}
                            showInfo={false}
                            strokeWidth={4}
                            size='small'
                        />
                    </Col>
                </Row>
            </div>
        );
    }

    private renderNavigation(): JSX.Element {
        const { taskInstance, history } = this.props;
        const { transcriptionStatus, retryingTranscription } = this.state;
        const { id } = taskInstance;
        const isTaskOpenable = transcriptionStatus?.state === 'Finished';
        const canRetryTranscription = transcriptionStatus?.state === 'Failed' && !retryingTranscription;
        const retryButton = (
            <Button
                className='cvat-item-retry-transcription-button'
                onClick={(e: React.MouseEvent): void => {
                    e.preventDefault();
                    this.retryTranscription().catch(() => {
                        // noop
                    });
                }}
                disabled={!canRetryTranscription}
                loading={retryingTranscription}
            >
                Retry transcription
            </Button>
        );
        const openDisabledReason = (() => {
            if (!transcriptionStatus) {
                return 'Transcription status is being checked.';
            }

            if (transcriptionStatus.state === 'Queued') {
                return 'Transcription is queued. You can open this task after it is completed.';
            }

            if (transcriptionStatus.state === 'Started') {
                return 'Transcription is in progress. You can open this task after it is completed.';
            }

            if (transcriptionStatus.state === 'Failed') {
                return 'Transcription failed. Please retry transcription before opening this task.';
            }

            return 'Task can be opened after transcription is completed.';
        })();

        return (
            <Col span={4}>
                {taskInstance.mode === 'interpolation' ? (
                    <Row justify='end'>
                        <Col>
                            {canRetryTranscription ? (
                                retryButton
                            ) : (
                                <CVATTooltip title={this.getRetryDisabledReason()}>
                                    <span>
                                        {retryButton}
                                    </span>
                                </CVATTooltip>
                            )}
                        </Col>
                    </Row>
                ) : null}
                <Row justify='end'>
                    <Col>
                        {isTaskOpenable ? (
                            <Button
                                className='cvat-item-open-task-button'
                                type='primary'
                                size='large'
                                ghost
                                onClick={(e: React.MouseEvent): void => {
                                    e.preventDefault();
                                    history.push(`/tasks/${id}`);
                                }}
                            >
                                Open
                            </Button>
                        ) : (
                            <CVATTooltip title={openDisabledReason}>
                                <span>
                                    <Button
                                        className='cvat-item-open-task-button'
                                        type='primary'
                                        size='large'
                                        ghost
                                        disabled
                                    >
                                        Open
                                    </Button>
                                </span>
                            </CVATTooltip>
                        )}
                    </Col>
                </Row>
                <Row justify='end'>
                    <Dropdown overlay={<ActionsMenuContainer taskInstance={taskInstance} />}>
                        <Col className='cvat-item-open-task-actions'>
                            <Text className='cvat-text-color'>Actions</Text>
                            <MoreOutlined className='cvat-menu-icon' />
                        </Col>
                    </Dropdown>
                </Row>
            </Col>
        );
    }

    public render(): JSX.Element {
        const { deleted, hidden } = this.props;
        const style = {};
        if (deleted) {
            (style as any).pointerEvents = 'none';
            (style as any).opacity = 0.5;
        }

        if (hidden) {
            (style as any).display = 'none';
        }

        return (
            <Row className='cvat-tasks-list-item' justify='center' align='top' style={{ ...style }}>
                {this.renderPreview()}
                {this.renderDescription()}
                {this.renderProgress()}
                {this.renderNavigation()}
            </Row>
        );
    }
}

export default withRouter(TaskItemComponent);
